// Webhook integration for data collection
const WEBHOOK_URL = 'https://hook.integrator.boost.space/la4dj2n6ifc5yaub4cgw5xm5edls9p5w';

// Helper function to get current timestamp in ISO format
const getCurrentTimestamp = () => {
  return new Date().toISOString();
};

// Helper function to get user's IP address (for GDPR compliance)
const getUserIP = async () => {
  try {
    // Note: In production, you might want to use a different service
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    return data.ip;
  } catch (error) {
    console.warn('Could not fetch IP address:', error);
    return 'unknown';
  }
};

// Helper function to prepare GDPR-compliant base data
const prepareGDPRData = async (formType, additionalData = {}) => {
  const timestamp = getCurrentTimestamp();
  const userIP = await getUserIP();
  
  return {
    ...additionalData,
    form_type: formType,
    submission_timestamp: timestamp,
    submission_date: new Date().toISOString().split('T')[0], // YYYY-MM-DD format
    submission_time: new Date().toLocaleTimeString('de-DE'), // HH:MM:SS format
    user_ip: userIP,
    user_agent: navigator.userAgent,
    page_url: window.location.href,
    referrer: document.referrer || 'direct',
    language: navigator.language || 'unknown',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'unknown'
  };
};

// Main webhook function - sends data directly to Make/Boost.space
export const sendWebhookData = async (eventType, data) => {
  try {
    // For form submissions, send the data directly without wrapper
    if (eventType === 'form_submission' || eventType === 'aktuelle_situation_assessment') {
      const response = await fetch(WEBHOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data) // Send data directly, no wrapper
      });

      if (!response.ok) {
        throw new Error(`Webhook failed: ${response.status}`);
      }

      console.log(`Form data sent successfully to webhook`);
      return true;
    }

    // For other events, use the wrapper format
    const payload = {
      event_type: eventType,
      timestamp: getCurrentTimestamp(),
      data: data,
      source: 'smarte-buchhaltung-online',
      user_agent: navigator.userAgent,
      page_url: window.location.href
    };

    const response = await fetch(WEBHOOK_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      throw new Error(`Webhook failed: ${response.status}`);
    }

    console.log(`Webhook sent successfully for ${eventType}`);
    return true;

  } catch (error) {
    console.error('Webhook error:', error);
    // Don't throw error to avoid breaking user experience
    return false;
  }
};

// Helper function to send contact form submissions
export const sendContactWebhook = async (contactData) => {
  const gdprData = await prepareGDPRData('contact_form', contactData);
  return sendWebhookData('contact_submission', gdprData);
};

// Helper function to send appointment requests
export const sendAppointmentWebhook = async (appointmentData) => {
  const gdprData = await prepareGDPRData('appointment_request', appointmentData);
  return sendWebhookData('appointment_request', gdprData);
};

// Helper function to send newsletter subscriptions
export const sendNewsletterWebhook = async (subscriberData) => {
  const gdprData = await prepareGDPRData('newsletter_subscription', subscriberData);
  return sendWebhookData('newsletter_subscription', gdprData);
};

// Helper function to send package requests
export const sendPackageWebhook = async (packageData) => {
  const gdprData = await prepareGDPRData('package_request', packageData);
  return sendWebhookData('package_request', gdprData);
};

// Helper function to send situation assessment - DIRECT TO WEBHOOK
export const sendSituationAssessment = async (assessmentData) => {
  try {
    // Prepare GDPR-compliant assessment data
    const gdprData = await prepareGDPRData('situation_assessment', assessmentData);

    // Send assessment data directly to webhook without any wrapper
    const response = await fetch(WEBHOOK_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(gdprData) // Direct JSON with GDPR data
    });

    if (!response.ok) {
      throw new Error(`Assessment webhook failed: ${response.status}`);
    }

    console.log('Assessment data sent successfully to webhook');
    return true;

  } catch (error) {
    console.error('Assessment webhook error:', error);
    return false;
  }
};

// Helper function to send quest demo interactions (keep existing format)
export const sendQuestDemoWebhook = (demoData) => {
  return sendWebhookData('quest_demo_interaction', demoData);
};

export default {
  sendWebhookData,
  sendContactWebhook,
  sendAppointmentWebhook,
  sendNewsletterWebhook,
  sendPackageWebhook,
  sendSituationAssessment,
  sendQuestDemoWebhook
};